import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'
import { useToast } from '@/components/ui/use-toast'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'

import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useCreateDocumentVersionSession } from '@/services/queries/document_sessions'
import { useTechnicianPageStore } from '../hooks/useTechnicianPageStore'

const formSchema = z.object({
  name: z.string().min(2),
})

export const StartForm = ({
  onStart,
  onCancel,
}: {
  onStart: () => void
  onCancel: () => void
}) => {
  const { data, isLoading } = useDocumentPageQuery()
  const { mutateAsync: createSession, isPaused: isCreatingSession } =
    useCreateDocumentVersionSession()
  const { toast } = useToast()

  const documentVersionId = data?.documentVersion?.id

  const setCurrentTechnician = useTechnicianPageStore(
    useShallow((state) => state.setCurrentTechnician),
  )

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (documentVersionId && values.name) {
      setCurrentTechnician(values.name)
      await createSession({
        documentVersionId,
        username: values.name,
      })
      onStart()
    } else {
      toast({
        title: `Can't start document session`,
        description: 'Missing document-version-id',
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="w-full max-w-96 flex flex-col space-y-4 justify-self-center">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="z-[99]">
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input className="rounded-full z-50" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center space-x-4 justify-self-end z-[99] relative">
          <Button
            type="button"
            variant="ghost"
            className="!text-sm text-gray-500 rounded-full"
            onClick={onCancel}
            size="lg"
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-full shadow-none" size="lg">
            {isCreatingSession ? (
              <div className="flex items-center space-x-3">
                <span>Starting</span>
                <Spinner className="w-3 h-3" />
              </div>
            ) : (
              <>Start</>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
